import { APP_DISPLAY_NAME } from "../branding";
import { WorkspaceShellStatus } from "./WorkspaceShellPrimitives";
import { canSendFeedback, reloadAndReport } from "./settings/FeedbackDialog";
import { Button } from "./ui/button";

/**
 * The whole-window error view, for a failure no narrower surface caught: the root route's
 * error component, and the boundary around everything `AppRoot` renders outside the router.
 */
export function AppErrorView({
  error,
  onTryAgain,
}: {
  readonly error: unknown;
  /** Offered only where the failed tree can be retried without reloading the app. */
  readonly onTryAgain?: () => void;
}) {
  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-4 py-10 text-foreground sm:px-6">
      <div className="pointer-events-none absolute inset-0 opacity-80">
        <div className="absolute inset-x-0 top-0 h-44 bg-[radial-gradient(44rem_16rem_at_top,color-mix(in_srgb,var(--color-red-500)_16%,transparent),transparent)]" />
        <div className="absolute inset-0 bg-[linear-gradient(145deg,color-mix(in_srgb,var(--background)_90%,var(--color-black))_0%,var(--background)_55%)]" />
      </div>

      <WorkspaceShellStatus
        kind="error"
        className="relative w-full max-w-xl rounded-2xl border border-border/80 bg-card/90 p-6 shadow-2xl shadow-black/20 backdrop-blur-md sm:p-8"
      >
        <p className="text-[11px] font-semibold tracking-[0.18em] text-muted-foreground uppercase">
          {APP_DISPLAY_NAME}
        </p>
        <h1 className="mt-3 text-2xl font-semibold tracking-tight sm:text-3xl">
          Something went wrong.
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{errorMessage(error)}</p>

        <div className="mt-5 flex flex-wrap gap-2">
          {onTryAgain === undefined ? null : (
            <Button size="sm" onClick={onTryAgain}>
              Try again
            </Button>
          )}
          <Button
            size="sm"
            variant={onTryAgain === undefined ? "default" : "outline"}
            onClick={() => window.location.reload()}
          >
            Reload app
          </Button>
          {canSendFeedback() ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() =>
                reloadAndReport({
                  origin: "crash",
                  summary: "The app crashed",
                  message: errorMessage(error),
                  occurredAt: new Date().toISOString(),
                })
              }
            >
              Reload and report
            </Button>
          ) : null}
          <Button
            size="sm"
            variant="ghost"
            onClick={() => void navigator.clipboard?.writeText(errorDetails(error)).catch(() => {})}
          >
            Copy details
          </Button>
        </div>

        <details className="group mt-5 overflow-hidden rounded-lg border border-border/70 bg-background/55">
          <summary className="cursor-pointer list-none px-3 py-2 text-xs font-medium text-muted-foreground">
            <span className="group-open:hidden">Show error details</span>
            <span className="hidden group-open:inline">Hide error details</span>
          </summary>
          <pre className="max-h-56 overflow-auto border-t border-border/70 bg-background/80 px-3 py-2 text-xs text-foreground/85">
            {errorDetails(error)}
          </pre>
        </details>
      </WorkspaceShellStatus>
    </div>
  );
}

function errorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  if (typeof error === "string" && error.trim().length > 0) {
    return error;
  }

  return "An unexpected error occurred.";
}

function errorDetails(error: unknown): string {
  if (error instanceof Error) {
    return error.stack ?? error.message;
  }

  if (typeof error === "string") {
    return error;
  }

  try {
    return JSON.stringify(error, null, 2);
  } catch {
    return "No additional error details are available.";
  }
}
